import { z } from 'zod';
import { analyticsListRequest, analyticsListResponse, analyticsQueryRequest, analyticsQueryResponse, } from '../crew/merchant/schema/analytics.js';
import { commandInvoiceDefinition } from './schema/invoiceDefinition.js';
import { commandOrganization } from './schema/organizations.js';
import { commandShipProtectSet } from './schema/protectSet.js';
import { commandSidekick } from './schema/sidekick.js';
import { commandShippingClaim } from './schema/shippingClaim.js';
import { commandStore } from './schema/store.js';
import { commandStoreAlert } from './schema/storeAlert.js';
import { commandUser } from './schema/user.js';
// * ----------- platform api ----------- //
const storePath = `/store`;
const invoiceDefinitionsPath = `/invoice-definition`;
const organizationRoute = '/organization';
const organizationIdPath = `${organizationRoute}/:organizationId`;
const organizationIdStorePath = `${organizationIdPath}/store`;
const organizationIdUserPath = `${organizationIdPath}/user`;
const organizationIdParams = z.object({ organizationId: z.coerce.number() });
const shipProtectSetPath = `/ship-protect-set`;
const shipProtectSetIdPath = `${shipProtectSetPath}/:setId`;
const shipProtectSetIdParams = z.object({ setId: z.coerce.number() });
const shipProtectSetStorePath = `${shipProtectSetIdPath}/store`;
const shipProtectSetUnitPath = `${shipProtectSetIdPath}/unit`;
const shipProtectSetUnitIdPath = `${shipProtectSetUnitPath}/:unitId`;
const shipProtectSetUnitIdParams = z.object({
    setId: z.coerce.number(),
    unitId: z.coerce.number(),
});
const usersPath = `/user`;
const usersIdPath = '/user/:userId';
const loginPath = `/login`;
const agencyPath = '/agency';
const frontTagPath = '/front-tag';
const storeAlertPath = '/store-alert';
const sidekickConversationPath = '/sidekick/conversation';
const sidekickConversationSearchPath = `${sidekickConversationPath}/search`;
const sidekickConversationIdPath = `${sidekickConversationPath}/:conversationId`;
const shippingClaimPath = '/shipping-claim';
const analyticsQueryPath = '/analytics/query';
const analyticsListQueryPath = '/analytics/lists/query';
export const commandPlatformApi = {
    // the merchant dashboards' queries over every active store
    [analyticsQueryPath]: {
        path: analyticsQueryPath,
        post: {
            request: { body: analyticsQueryRequest },
            response: { body: analyticsQueryResponse },
        },
    },
    [analyticsListQueryPath]: {
        path: analyticsListQueryPath,
        post: {
            request: { body: analyticsListRequest },
            response: { body: analyticsListResponse },
        },
    },
    [agencyPath]: {
        path: agencyPath,
        get: {
            request: {},
            response: {
                body: z.array(z.object({
                    id: z.number(),
                    name: z.string(),
                })),
            },
        },
    },
    // all available Front tags, for selecting a store's report tags
    [frontTagPath]: {
        path: frontTagPath,
        get: {
            request: {},
            response: {
                body: commandStore.base.shape.frontReportTags,
            },
        },
    },
    // CX-authored store alert broadcast (all stores or a subset)
    [storeAlertPath]: {
        path: storeAlertPath,
        post: {
            request: {
                body: commandStoreAlert.create,
            },
            response: {
                body: commandStoreAlert.result,
            },
        },
    },
    // Sidekick conversations and what they cost over a period
    [sidekickConversationSearchPath]: {
        path: sidekickConversationSearchPath,
        post: {
            request: {
                body: commandSidekick.search,
            },
            response: {
                body: commandSidekick.list,
            },
        },
    },
    [sidekickConversationIdPath]: {
        path: sidekickConversationIdPath,
        get: {
            request: {
                params: z.object({ conversationId: z.coerce.number() }),
            },
            response: {
                body: commandSidekick.detail,
            },
        },
    },
    // cursor-paginated shipping claims across every store; POST so array filters survive the trip
    [shippingClaimPath]: {
        path: shippingClaimPath,
        post: {
            request: {
                body: commandShippingClaim.listRequest,
            },
            response: {
                body: commandShippingClaim.listResponse,
            },
        },
    },
    // top level store search
    [storePath]: {
        path: storePath,
        get: {
            request: {
                query: z.object({ searchTerm: z.string().optional() }),
            },
            response: {
                body: z.array(commandStore.list),
            },
        },
    },
    // ----------- Login Route -----------//
    // the acting admin comes from the verified bearer token, not the request
    [loginPath]: {
        path: loginPath,
        post: {
            request: {},
            response: {
                body: commandUser.internalAdmin,
            },
        },
    },
    // ----------- User Routes -----------//
    // (this returns all users for the platform with all of their roles)
    [usersPath]: {
        path: usersPath,
        get: {
            request: {
                query: z.object({ searchTerm: z.string().optional() }),
            },
            response: {
                body: z.array(commandUser.base),
            },
        },
    },
    [usersIdPath]: {
        path: usersIdPath,
        get: {
            request: {
                params: z.object({ userId: z.coerce.number() }),
            },
            response: {
                body: commandUser.baseWithAllRoles,
            },
        },
    },
    // ----------- Invoice Definitions -----------//
    [invoiceDefinitionsPath]: {
        path: invoiceDefinitionsPath,
        get: {
            request: {},
            response: {
                body: z.array(commandInvoiceDefinition.base),
            },
        },
    },
    // ----------- Protection Sets -----------//
    [shipProtectSetPath]: {
        path: shipProtectSetPath,
        post: {
            request: {
                body: commandShipProtectSet.create,
            },
            response: {
                body: commandShipProtectSet.base,
            },
        },
        get: {
            request: {},
            response: {
                body: z.array(commandShipProtectSet.base),
            },
        },
    },
    [shipProtectSetIdPath]: {
        path: shipProtectSetIdPath,
        get: {
            request: {
                params: shipProtectSetIdParams,
            },
            response: {
                body: commandShipProtectSet.detail,
            },
        },
    },
    [shipProtectSetStorePath]: {
        path: shipProtectSetStorePath,
        get: {
            request: {
                params: shipProtectSetIdParams,
            },
            response: {
                body: z.array(commandStore.list),
            },
        },
    },
    [shipProtectSetUnitPath]: {
        path: shipProtectSetUnitPath,
        post: {
            request: {
                params: shipProtectSetIdParams,
                body: commandShipProtectSet.unitInput,
            },
            response: {
                body: commandShipProtectSet.detail,
            },
        },
    },
    [shipProtectSetUnitIdPath]: {
        path: shipProtectSetUnitIdPath,
        put: {
            request: {
                params: shipProtectSetUnitIdParams,
                body: commandShipProtectSet.unitInput,
            },
            response: {
                body: commandShipProtectSet.detail,
            },
        },
        delete: {
            request: {
                params: shipProtectSetUnitIdParams,
            },
            response: {
                body: commandShipProtectSet.detail,
            },
        },
    },
    // ----------- organization -----------//
    [organizationRoute]: {
        path: organizationRoute,
        post: {
            request: {
                body: commandOrganization.create,
            },
            response: {
                body: commandOrganization.list,
            },
        },
        get: {
            request: {},
            response: {
                body: z.array(commandOrganization.list),
            },
        },
    },
    [organizationIdPath]: {
        path: organizationIdPath,
        get: {
            request: {
                params: organizationIdParams,
            },
            response: {
                body: commandOrganization.base,
            },
        },
    },
    // add store
    [organizationIdStorePath]: {
        path: organizationIdStorePath,
        get: {
            request: {
                params: organizationIdParams,
            },
            response: {
                body: z.array(commandStore.list),
            },
        },
        post: {
            request: {
                params: organizationIdParams,
                body: z.object({ storeId: z.number() }),
            },
            response: {
                body: commandOrganization.base,
            },
        },
        delete: {
            request: {
                params: organizationIdParams,
                body: z.object({ storeId: z.number() }),
            },
            response: {
                body: commandOrganization.base,
            },
        },
    },
    // add user
    [organizationIdUserPath]: {
        path: organizationIdUserPath,
        post: {
            request: {
                params: organizationIdParams,
                body: commandOrganization.userList.pick({
                    email: true,
                    firstName: true,
                    lastName: true,
                    roleCode: true,
                }),
            },
            response: {
                body: commandOrganization.base,
            },
        },
        delete: {
            request: {
                params: organizationIdParams,
                body: commandOrganization.userList.pick({ email: true }),
            },
            response: {
                body: commandOrganization.base,
            },
        },
    },
};
// * ----------- store api ----------- //
const storeIdPath = `${storePath}/:storeId`;
const storeIdInvoicePath = `${storeIdPath}/invoice`;
const storeIdPricingSetPath = `${storeIdPath}/pricing-set`;
const storeIdLocationIdPath = `${storeIdPath}/location/:locationId`;
const storeIdShippingLineIdPath = `${storeIdPath}/shipping-line/:shippingLineId`;
const storeIdStoreContactPath = `${storeIdPath}/contact`;
const storeIdStoreContactIdPath = `${storeIdStoreContactPath}/:contactId`;
const storeIdAgencyPath = `${storeIdPath}/agency`;
const storeIdUserPath = `${storeIdPath}/user`;
const storeIdExtensionPath = `${storeIdPath}/extension`;
const storeIdShipProtectSettingsPath = `${storeIdPath}/ship-protect-settings`;
const storeIdFrontSettingsPath = `${storeIdPath}/front-settings`;
const storeIdFeatureFlagsPath = `${storeIdPath}/feature-flags`;
const storeIdEmailSettingsPath = `${storeIdPath}/email-settings`;
const storeIdBillingProviderPath = `${storeIdPath}/billing-provider`;
const storeIdInspectionGradePath = `${storeIdPath}/inspection-grade`;
const storeIdInspectionGradeIdPath = `${storeIdPath}/inspection-grade/:inspectionGradeId`;
const storeIdInvoiceDefinitionPath = `${storeIdPath}/invoice-definition`;
const storeIdInvoiceDefinitionIdPath = `${storeIdInvoiceDefinitionPath}/:invoiceDefinitionId`;
const storeIdCommissionArrangementPath = `${storeIdPath}/commission-arrangement`;
const storeIdCommissionArrangementIdPath = `${storeIdCommissionArrangementPath}/:commissionArrangementId`;
const storeIdParams = z.object({ storeId: z.coerce.number() });
export const commandStoreApi = {
    [storeIdPath]: {
        path: storeIdPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Pricing Set ------- //
    [storeIdPricingSetPath]: {
        path: storeIdPricingSetPath,
        put: {
            request: {
                params: storeIdParams,
                body: z.object({ shipProtectSetId: z.number() }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------ Invoices -------  //
    [storeIdInvoicePath]: {
        path: storeIdInvoicePath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: z.array(commandStore.invoice),
            },
        },
    },
    // ------ Users -------  //
    [storeIdUserPath]: {
        path: storeIdUserPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: z.array(commandStore.user),
            },
        },
    },
    // ------ Extension Heartbeats -------  //
    [storeIdExtensionPath]: {
        path: storeIdExtensionPath,
        get: {
            request: {
                params: storeIdParams,
            },
            response: {
                body: z.array(commandStore.extension),
            },
        },
    },
    // ------- Update Fulfillment Location ------- //
    [storeIdLocationIdPath]: {
        path: storeIdLocationIdPath,
        put: {
            request: {
                params: z.object({
                    storeId: z.coerce.number(),
                    locationId: z.coerce.number(),
                }),
                body: z.object({ allowReorder: z.boolean() }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Update Shipping Line ------- //
    [storeIdShippingLineIdPath]: {
        path: storeIdShippingLineIdPath,
        put: {
            request: {
                params: z.object({
                    storeId: z.coerce.number(),
                    shippingLineId: z.coerce.number(),
                }),
                body: z.object({ allowReorder: z.boolean() }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------ Create Store Contact -------  //
    [storeIdStoreContactPath]: {
        path: storeIdStoreContactPath,
        post: {
            request: {
                params: storeIdParams,
                body: commandStore.storeContact.omit({ id: true }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------ Update / Delete Store Contact -------  //
    [storeIdStoreContactIdPath]: {
        path: storeIdStoreContactIdPath,
        put: {
            request: {
                params: z.object({
                    storeId: z.coerce.number(),
                    contactId: z.coerce.number(),
                }),
                body: commandStore.storeContact.omit({ id: true }),
            },
            response: {
                body: commandStore.base,
            },
        },
        delete: {
            request: {
                params: z.object({
                    storeId: z.coerce.number(),
                    contactId: z.coerce.number(),
                }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Update Agency ------- //
    [storeIdAgencyPath]: {
        path: storeIdAgencyPath,
        put: {
            request: {
                params: storeIdParams,
                body: z.object({ agencyId: z.number().nullable() }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Update Shipping Protection Settings ------- //
    [storeIdShipProtectSettingsPath]: {
        path: storeIdShipProtectSettingsPath,
        put: {
            request: {
                params: storeIdParams,
                body: commandStore.base.shape.shippingProtectionSettings,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Feature Flags ------- //
    [storeIdFeatureFlagsPath]: {
        path: storeIdFeatureFlagsPath,
        put: {
            request: {
                params: storeIdParams,
                body: commandStore.base.pick({ featureFlags: true }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Front Settings ------- //
    [storeIdFrontSettingsPath]: {
        path: storeIdFrontSettingsPath,
        put: {
            request: {
                params: storeIdParams,
                body: z.object({
                    frontReportTags: commandStore.base.shape.frontReportTags,
                }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Email Settings ------- //
    [storeIdEmailSettingsPath]: {
        path: storeIdEmailSettingsPath,
        put: {
            request: {
                params: storeIdParams,
                body: commandStore.base.pick({ verifiedEmailDomain: true }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Billing Provider ------- //
    [storeIdBillingProviderPath]: {
        path: storeIdBillingProviderPath,
        put: {
            request: {
                body: commandStore.billingProvider.update,
                params: storeIdParams,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ----- Create Inspection Grade ----- //
    [storeIdInspectionGradePath]: {
        path: storeIdInspectionGradePath,
        post: {
            request: {
                body: commandStore.inspectionGrade.omit({
                    id: true,
                }),
                params: storeIdParams,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ----- Update Inspection Grade ----- //
    [storeIdInspectionGradeIdPath]: {
        path: storeIdInspectionGradeIdPath,
        put: {
            request: {
                body: commandStore.inspectionGrade.omit({
                    id: true,
                    code: true,
                }),
                params: storeIdParams.extend({ inspectionGradeId: z.coerce.number() }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Invoice Definitions ------- //
    [storeIdInvoiceDefinitionPath]: {
        path: storeIdInvoiceDefinitionPath,
        post: {
            request: {
                body: commandStore.invoiceDefinition.create,
                params: storeIdParams,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    [storeIdInvoiceDefinitionIdPath]: {
        path: storeIdInvoiceDefinitionIdPath,
        put: {
            request: {
                body: z.object({ extensionInDays: z.number() }),
                params: storeIdParams.extend({
                    invoiceDefinitionId: z.coerce.number(),
                }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    // ------- Commission Arrangement ------- //
    [storeIdCommissionArrangementPath]: {
        path: storeIdCommissionArrangementPath,
        post: {
            request: {
                body: commandStore.commissionArrangement.create,
                params: storeIdParams,
            },
            response: {
                body: commandStore.base,
            },
        },
    },
    [storeIdCommissionArrangementIdPath]: {
        path: storeIdCommissionArrangementIdPath,
        put: {
            request: {
                body: commandStore.commissionArrangement.update,
                params: storeIdParams.extend({
                    commissionArrangementId: z.coerce.number(),
                }),
            },
            response: {
                body: commandStore.base,
            },
        },
    },
};
//# sourceMappingURL=api.js.map